<template>
    <v-data-table
        id="plot-grid-table"
        class="elevation-0 pt-4 mt-16"
        hide-default-footer
        :custom-sort="customSort"
        :headers="headerItems"
        :items="tableItems"
        :items-per-page="plotPerPage"
        :page.sync="currentPage"
        :sort-by.sync="sortBy"
    >
        <template v-slot:header.surface="{ header }">
            <div class="area-header">
                <span v-html="header.text"/>
                <SwitchAreaUnit
                    v-if="language === 'en'"
                    :areaUnit="areaUnit"
                    location="table"
                />
            </div>
        </template>

        <!-- Plot name + preview + availability template -->
        <template v-slot:item.name="{ item }">
            <v-row class="justify-center">
                <div
                    class="d-inline px-2"
                    :class="[$vuetify.breakpoint.xl ? 'my-6' : 'my-4']"
                >
                    <v-img
                        class="plot-preview"
                        :class="{ 'disabled': disabledAvailabilities.includes(item.availability.type) }"
                        contain
                        :width="$vuetify.breakpoint.xl ? 120 : 90"
                        :height="$vuetify.breakpoint.xl ? 80 : 50"
                        :src="item.preview"
                    />
                </div>
                <div
                    class="d-inline"
                    :class="[$vuetify.breakpoint.xl ? 'my-8' : 'my-4']"
                >
                    <span v-html="item.name"/><br>
                    <span
                        class="habiteo-body-2"
                        :style="{ 'color': item.availability.color }"
                        v-html="item.availability.text"
                    />
                </div>
            </v-row>
        </template>
        <!-- /Plot name + preview + availability template -->

        <!-- Surface + annex surface template -->
        <template v-slot:item.surface="{ item }">
            <div v-html="item.surface.main"/>
            <div
                v-if="item.surface.annex"
                class="habiteo-body-2 grey-text"
            >
                {{ $t('sidebar.filters.annex') }} : <span v-html="item.surface.annex"/>
            </div>
        </template>
        <!-- /Surface + annex surface template -->

        <!-- Tags template -->
        <template v-slot:item.tags="{ item }">
            <div v-if="item.tags">
                <label
                    v-for="(tag, tagName) in item.tags"
                    :key="tagName"
                    class="tag"
                    :style="{ 'background-color': tag.color }"
                >
                    {{ tagName }}
                </label>
            </div>
        </template>
        <!-- /Tags template-->

        <!-- Plot link template -->
        <template v-slot:item.plotLink="{ item }">
            <v-btn
                v-if="item.funnelLink.url"
                rounded
                outlined
                medium
                class="elevation-0 text-none plot-cta"
                @click.prevent="openFunnel(item.funnelLink.url)"
            >
                <span class="habiteo-body-1">
                    {{ item.funnelLink.text }}
                </span>
            </v-btn>


            <v-btn
                v-else-if="item.externalLink"
                rounded
                outlined
                medium
                class="elevation-0 text-none plot-cta"
                @click.prevent="openExternalLink(item.externalLink)"
            >
                <span class="habiteo-body-1">
                    {{ $t('widgets.plot.discover') }}
                </span>
            </v-btn>

            <v-btn
                v-else-if="item.plotLink.url"
                rounded
                outlined
                medium
                class="elevation-0 text-none plot-cta"
                @click.prevent="loadPlot(item.plot)"
            >
                <span class="habiteo-body-1">
                    {{ $t('widgets.plot.discover') }}
                </span>
            </v-btn>
        </template>
        <!-- Plot link template -->

        <!-- Favorite template -->
        <template v-slot:item.favorite="{ item }">
            <v-tooltip 
                v-if="isAvailabilityEnabled(item.plot)"
                content-class="top"
                top
            >
                <template v-slot:activator="{ on, attrs }">
                    <a
                        id="gtm-plotgrid-cta-favorite"
                        v-bind="attrs"
                        v-on="on"
                        class="favorite-pg"
                        @click.prevent="toggleFavorite(item.plot)"
                    >
                        <v-icon
                            small
                            class="icon"
                        >
                            {{ isFavorite(item.plot) ? 'icon-favorite active-heart' : 'icon-empty-favorite' }}
                        </v-icon>
                    </a>
                </template>
                <span>
                    {{ getTooltip(item.plot) }}
                </span>
            </v-tooltip>
        </template>
        <!-- Favorite template -->
    </v-data-table>
</template>

<script>
    import { EventBus } from '@/event-bus';
    import { mapGetters } from 'vuex';

    import SwitchAreaUnit from "@/components/filters/SwitchAreaUnit";

    export default {
        name: 'PlotGridTable',
        components: {
            SwitchAreaUnit
        },
        props: {
            areaUnit: {
                type: String,
                required: false
            },
            headers: {
                type: Array,
                required: true
            },
            plotList: {
                type: Array,
                required: true
            },
            currentPage: {
                type: Number,
                required: true
            },
            isOpenedSidebar: {
                type: Boolean,
                required: true
            },
            programUuid: {
                type: String,
                required: true
            },
            plotPerPage: {
                type: Number,
                required: true
            },
            isLandscape: {
                type: Boolean,
                required: true
            }
        },
        data: () => ({
            plotFavList: [],
            skippedPlotDetails: [
                'level',
                'surface_annex',
                'description',
                'metrics',
                'availability',
                'max_workstation',
                'bathrooms',
                'bedrooms',
                'annexes',
                'annex1',
                'annex2',
                'lotPrice',
                'annexPrice'
            ],
            disabledAvailabilities: [
                2,      // Unavailable
                4,      // Reserved
                5       // Sold
            ],
            surfaceUnit: 'feet',
            squareMeterValue: .093,
            tableItems: [],
            sortBy: null
        }),
        computed: {
            ...mapGetters([
                'isLocalstorageAvailable',
                'language'
            ]),
            headerItems() {
                var headers = [];
                if (this.$props.headers) {
                    this.$props.headers.forEach(header => {
                        if (!this.skippedPlotDetails.includes(header.id)) {
                            let headerObj = {
                                'align': 'center',
                                'text': header.label,
                                'value': header.id,
                                'sortable': true,
                                'width': 140
                            };
                            switch (header.id) {
                                case 'name':
                                    headerObj['width'] = 160;
                                    headers.push(headerObj);
                                    break;

                                case 'category':
                                case 'surface':
                                    headerObj['width'] = 140;
                                    headers.push(headerObj);
                                    break;

                                case 'tags':
                                    if ((this.$vuetify.breakpoint.md && this.isLandscape) || (this.$vuetify.breakpoint.lg && !this.$props.isOpenedSidebar) || this.$vuetify.breakpoint.xl) {
                                        headerObj['text'] = this.$t('sidebar.filters.tag');
                                        headerObj['sortable'] = false;
                                        headers.push(headerObj);
                                    }
                                    break;

                                case 'exposition':
                                    if ((this.$vuetify.breakpoint.md && this.isLandscape) || this.$vuetify.breakpoint.lgAndUp)
                                        headers.push(headerObj);
                                    break;

                                case 'totalPrice':
                                    if ((this.$vuetify.breakpoint.sm && this.isLandscape) || this.$vuetify.breakpoint.mdAndUp) {
                                        headerObj['width'] = 100;
                                        headers.push(headerObj);
                                    }
                                    break;

                                default:
                                    headers.push(headerObj);
                                    break;
                            }
                        }
                    });
                    headers.push({
                        'align': 'center',
                        'value': 'plotLink',
                        'sortable': false,
                        'width': 40
                    });
                    headers.push({
                        'align': 'center',
                        'value': 'favorite',
                        'sortable': false,
                        'width': 40
                    });
                }
                return headers;
            },
            favoriteKey() {
                return `favorite-${this.programUuid}`;
            },
            favoriteList: {
                get() {
                    return this.plotFavList;
                },
                set(newValue) {
                    localStorage.setItem(this.favoriteKey, JSON.stringify(newValue));
                    this.plotFavList = JSON.parse(localStorage.getItem(this.favoriteKey));
                }
            }
        },
        mounted() {
            this.fillTableItems();
            this.refreshFavorites();
        },
        methods: {
            fillTableItems() {
                this.tableItems = [];
                if (this.plotList) {
                    this.plotList.filter(plot => plot.visible).forEach(plot => {
                        this.tableItems.push({
                            'name': plot.name,
                            'preview': plot.preview,
                            'availability': {
                                'type': this.getPlotDetails('availability', 'type', plot),
                                'text': this.getPlotDetails('availability', 'value', plot),
                                'color': this.getPlotDetails('availability', 'color', plot)
                            },
                            'category': this.getPlotDetails('category', 'value', plot),
                            'surface': {
                                'main': this.displayFormatedArea(this.getPlotDetails('surface', 'value', plot)),
                                'annex': this.displayFormatedArea(this.getPlotDetails('surface_annex', 'value', plot)),
                            },
                            'exposition': this.getPlotDetails('exposition', 'value', plot),
                            'tags': this.getPlotDetails('tags', 'tags', plot),
                            'totalPrice': this.getPlotDetails('totalPrice', 'value', plot),
                            'plotLink': {
                                'url': plot.link,
                                'text': plot.linkText
                            },
                            'funnelLink': {
                                'url': this.getPlotDetails('funnel', 'link', plot),
                                'text': this.getPlotDetails('funnel', 'text', plot),
                            },
                            'externalLink': this.getPlotDetails('external_link', 'value', plot),
                            'plot': plot,
                        });
                    });
                    // Force async sort
                    var currentSort = this.sortBy;
                    this.sortBy = null;
                    this.sortBy = currentSort;
                }
            },
            displayFormatedArea(value) {
                if (!value)
                    return '';

                let area = value.replace(/\s/g, '');
                switch (this.language) {
                    case 'es':
                    case 'it':
                        area = area.replace('.', '');
                        area = parseFloat(area.replace(',', '.'));
                        area = area.toString().replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                    break;

                    case 'en':
                        area = parseFloat(area.replace(',', ''));
                        if (this.areaUnit === 'sq m')
                            area = Math.round(((area * this.squareMeterValue) + Number.EPSILON) * 100) / 100;
                        area = area.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    break;

                    default:
                        area = parseFloat(area.replace(',', '.'));
                        area = area.toString().replace('.', ',');
                    break;
                }
                return area + ' ' + this.$props.areaUnit;
            },
            customSort(items, index, isDescending) {
                items.sort((a, b) => {
                    let field = index[0];
                    switch (field) {
                        case 'price':
                        case 'totalPrice':
                            let priceA = a.totalPrice ? parseFloat(a.totalPrice.replace(/\s/g, '')) : 0;
                            let priceB = b.totalPrice ? parseFloat(b.totalPrice.replace(/\s/g, '')) : 0;
                            if (!isDescending[0])
                                return priceA - priceB;
                            else
                                return priceB - priceA;

                        case 'surface':
                            let areaA = a.surface.main.replace(/\s/g, '');
                            let areaB = b.surface.main.replace(/\s/g, '');

                            if (this.language === 'en')  {
                                // Remove ',' thousand separator
                                areaA = areaA.split(this.$props.areaUnit)[0].replace(',', '');
                                areaB = areaB.split(this.$props.areaUnit)[0].replace(',', '');
                            }
                            else {
                                areaA = areaA.replace('.', '');
                                areaB = areaB.replace('.', '');
                            }

                            areaA = parseFloat(areaA.match(/[0-9]+/g));
                            areaB = parseFloat(areaB.match(/[0-9]+/g));

                            if (!isDescending[0])
                                return areaA - areaB;
                            else
                                return areaB - areaA;

                        default:
                            if (typeof a[field] !== 'undefined') {
                                let valueA = a[field] ? a[field] : '';
                                let valueB = b[field] ? b[field] : '';
                                if (!isDescending[0])
                                    return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
                                else
                                    return valueB.toLowerCase().localeCompare(valueA.toLowerCase());
                            }
                    }
                });
                EventBus.$emit('refreshPagination', this.currentPage);
                return items;
            },
            loadPlot(link) {
                this.$emit('update-widget', link);
            },
            displayFavoriteBanner() {
                let localStorageKey = 'favorite-banner-' + this.$route.query.id + '-' + this.$route.query.key;
                let nextDisplayDate = this.isLocalstorageAvailable ? localStorage.getItem(localStorageKey) : null;
                let today = new Date().getTime();
                if (!nextDisplayDate || nextDisplayDate <= today) {
                    // 30 days between each time the banner is displayed
                    let lifeCycle = 30 * 24 * 60 * 60 * 1000;
                    let nextDate = new Date(new Date().getTime() + lifeCycle);
                    if (this.isLocalstorageAvailable)
                        localStorage.setItem(localStorageKey, nextDate.getTime());
                    EventBus.$emit('displayFlashMessage', this.$t("flashMessage.favorite"));
                }
            },
            toggleFavorite(plot) {
                var plotId = null;
                if (this.isAvailabilityEnabled(plot)) {
                    let url = new URL(plot.link);
                    plotId = url.searchParams.get('id');
                }

                if (plotId) {
                    if (!this.favoriteList.includes(plotId)) {
                        var plotList = this.favoriteList ? this.favoriteList : [];
                        plotList.push(plotId);
                        this.favoriteList = plotList;
                        this.displayFavoriteBanner();
                    }
                    else {
                        var plotList = this.favoriteList.filter(favPlotId => {
                            return favPlotId !== plotId
                        });

                        this.favoriteList = plotList;
                    }
                    EventBus.$emit('updatePlotFavorites', this.favoriteList);
                }
            },
            isAvailabilityEnabled(plot) {
                return plot.link != null;
            },
            isFavorite(plot) {
                let status = false;
                let plotFavList = JSON.parse(localStorage.getItem(this.favoriteKey));
                if (plot.link) {
                    let plotId = new URL(plot.link).searchParams.get('id');
                    status = plotFavList.includes(plotId);
                }
                return status;
            },
            getTooltip(plot) {
                if (this.isFavorite(plot))
                    return this.$t("favorite.removeFavorite")
                else
                    return this.$t("favorite.addFavorite")
            },
            getPlotDetails(key, property, plot) {
                var result = plot.details.find(detail => detail.id === key);
                return result ? result[property] : null;
            },
            refreshFavorites() {
                if (this.isLocalstorageAvailable)
                    this.plotFavList = localStorage.getItem(this.favoriteKey) ? JSON.parse(localStorage.getItem(this.favoriteKey)) : [];
            },
            openFunnel(url) {
                window.open(url);
            },
            openExternalLink(url) {
                EventBus.$emit('openExternalLink', url);
            }
        },
        watch: {
            areaUnit() {
                if (this.language === 'en') {
                    this.fillTableItems();
                }
            },
            plotList() {
                this.fillTableItems();
            }
        }
    };
</script>


// WEBPACK FOOTER //
// src/components/plotGrid/PlotGridTable.vue